import clsx from "clsx"
import type { KeyboardEvent, MouseEvent } from "react"

import { ifSpaceOrEnter } from "@utils/keyboard"

import { ListItemContext } from "./ListItem.context"
import styles from "./ListItem.module.scss"
import { ListItemProps } from "./ListItem.types"
import ListItemActions from "./ListItemActions/ListItemActions"
import ListItemAvatarAndContent from "./ListItemAvatarAndContent/ListItemAvatarAndContent"
import ListItemCaption from "./ListItemCaption/ListItemCaption"
import ListItemLabel from "./ListItemLabel/ListItemLabel"

function ListItem(props: ListItemProps) {
    const {
        children,
        className,
        orientation = "horizontal",
        isLoading = false,
        _isFocused = false,
        _isHovered = false,
        isPressed = false,
        isDisabled = false,
        isClickable = false,
        onClick,
        ...rest
    } = props

    const canInteract = isClickable && !isDisabled

    function handleClick(event: MouseEvent<HTMLDivElement>) {
        if (canInteract) {
            onClick?.(event)
        }
    }

    function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
        if (canInteract) {
            ifSpaceOrEnter(event, () => {
                onClick?.()
            })
        }
    }

    const rootClassName = clsx(
        styles.base,
        styles[orientation],
        {
            [styles.clickable]: isClickable,
            [styles.isFocused]: _isFocused,
            [styles.isHovered]: _isHovered,
            [styles.isPressed]: isPressed,
            [styles.isDisabled]: isDisabled,
        },
        className,
    )

    return (
        <ListItemContext.Provider value={{ isLoading }}>
            <div
                className={rootClassName}
                role={isClickable ? "button" : undefined}
                tabIndex={isClickable ? (isDisabled ? -1 : 0) : undefined}
                aria-disabled={isClickable ? isDisabled : undefined}
                onClick={canInteract ? handleClick : undefined}
                onKeyDown={canInteract ? handleKeyDown : undefined}
                {...rest}
            >
                {children}
            </div>
        </ListItemContext.Provider>
    )
}

export default {
    Root: ListItem,
    Caption: ListItemCaption,
    Label: ListItemLabel,
    Actions: ListItemActions,
    AvatarAndContent: ListItemAvatarAndContent,
}
